import React from 'react';
import { motion } from 'framer-motion';
import { Recommendation } from '../../types/creditScore';
import { TrendingUp, Clock, CheckCircle, ChevronRight, Zap, Target, Award } from 'lucide-react';
interface ImprovementTipsProps {
  recommendations: Recommendation[];
  onActionClick?: (recommendation: Recommendation) => void;
}
const impactColors = {
  high: 'bg-red-50 border-red-200 text-red-700',
  medium: 'bg-yellow-50 border-yellow-200 text-yellow-700',
  low: 'bg-blue-50 border-blue-200 text-blue-700'
};
const impactIcons = {
  high: Zap,
  medium: Target,
  low: Award
};
export function ImprovementTips({
  recommendations,
  onActionClick
}: ImprovementTipsProps) {
  return <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-id="element-774">
      <div className="flex items-center justify-between mb-6" data-id="element-775">
        <div data-id="element-776">
          <h3 className="text-lg font-bold text-gray-900" data-id="element-777">
            Improve Your Score
          </h3>
          <p className="text-sm text-gray-500 mt-1" data-id="element-778">
            Follow these personalized recommendations
          </p>
        </div>
        <div className="h-10 w-10 bg-green-50 rounded-lg flex items-center justify-center" data-id="element-779">
          <TrendingUp className="h-5 w-5 text-green-600" data-id="element-780" />
        </div>
      </div>

      <div className="space-y-4" data-id="element-781">
        {recommendations.map((rec, index) => {
        const ImpactIcon = impactIcons[rec.impact];
        return <motion.div key={rec.id} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: index * 0.1
        }} className={`border-2 rounded-xl p-4 ${rec.completed ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200 hover:border-blue-300 hover:shadow-md'} transition-all cursor-pointer`} onClick={() => !rec.completed && onActionClick?.(rec)} data-id="element-782">
              <div className="flex items-start space-x-4" data-id="element-783">
                {/* Impact Badge */}
                <div className={`px-3 py-1 rounded-full text-xs font-bold border ${impactColors[rec.impact]} flex items-center space-x-1`} data-id="element-784">
                  <ImpactIcon className="h-3 w-3" data-id="element-785" />
                  <span className="uppercase" data-id="element-786">{rec.impact} Impact</span>
                </div>

                <div className="flex-1" data-id="element-787">
                  {/* Title & Score Increase */}
                  <div className="flex items-start justify-between mb-2" data-id="element-788">
                    <h4 className="font-semibold text-gray-900" data-id="element-789">{rec.title}</h4>
                    <div className="flex items-center space-x-1 text-green-600" data-id="element-790">
                      <TrendingUp className="h-4 w-4" data-id="element-791" />
                      <span className="text-sm font-bold" data-id="element-792">
                        +{rec.scoreIncrease}
                      </span>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-sm text-gray-600 mb-3" data-id="element-793">
                    {rec.description}
                  </p>

                  {/* Meta Information */}
                  <div className="flex items-center justify-between" data-id="element-794">
                    <div className="flex items-center space-x-4 text-xs text-gray-500" data-id="element-795">
                      <div className="flex items-center space-x-1" data-id="element-796">
                        <Clock className="h-3 w-3" data-id="element-797" />
                        <span data-id="element-798">{rec.timeToImpact}</span>
                      </div>
                      {rec.completed && <div className="flex items-center space-x-1 text-green-600" data-id="element-799">
                          <CheckCircle className="h-3 w-3" data-id="element-800" />
                          <span data-id="element-801">Completed</span>
                        </div>}
                    </div>

                    {!rec.completed && <button className="flex items-center space-x-1 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-802">
                        <span data-id="element-803">{rec.actionRequired}</span>
                        <ChevronRight className="h-4 w-4" data-id="element-804" />
                      </button>}
                  </div>
                </div>
              </div>
            </motion.div>;
      })}
      </div>

      {/* Potential Score */}
      <div className="mt-6 pt-6 border-t border-gray-200" data-id="element-805">
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200" data-id="element-806">
          <div className="flex items-center justify-between" data-id="element-807">
            <div data-id="element-808">
              <p className="text-sm text-green-700 font-medium mb-1" data-id="element-809">
                Potential Score Increase
              </p>
              <p className="text-xs text-green-600" data-id="element-810">
                Complete all recommendations to maximize your score
              </p>
            </div>
            <div className="text-right" data-id="element-811">
              <div className="text-3xl font-bold text-green-700" data-id="element-812">
                +
                {recommendations.reduce((sum, rec) => sum + rec.scoreIncrease, 0)}
              </div>
              <div className="text-xs text-green-600" data-id="element-813">points</div>
            </div>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="mt-6 bg-blue-50 rounded-lg p-4 border border-blue-200" data-id="element-814">
        <h4 className="font-semibold text-blue-900 mb-3 flex items-center" data-id="element-815">
          <Award className="h-4 w-4 mr-2" data-id="element-816" />
          Benefits of Higher Score
        </h4>
        <ul className="space-y-2 text-sm text-blue-800" data-id="element-817">
          <li className="flex items-start" data-id="element-818">
            <CheckCircle className="h-4 w-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-819" />
            <span data-id="element-820">Lower interest rates (save up to ₦50,000 annually)</span>
          </li>
          <li className="flex items-start" data-id="element-821">
            <CheckCircle className="h-4 w-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-822" />
            <span data-id="element-823">Instant approval for rent requests</span>
          </li>
          <li className="flex items-start" data-id="element-824">
            <CheckCircle className="h-4 w-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-825" />
            <span data-id="element-826">No guarantor or insurance required</span>
          </li>
          <li className="flex items-start" data-id="element-827">
            <CheckCircle className="h-4 w-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-828" />
            <span data-id="element-829">Higher rent amounts approved</span>
          </li>
        </ul>
      </div>
    </div>;
}